import * as yup from 'yup';
import {
  CreateMatchupCompetitorInput,
  CreateMatchupInput,
  MatchupCorner,
} from 'api/graphql-global-types';

export type CompetitorDraft = {
  storeId?: string;
  name: string;
  avatarURL?: string;
  invitedEmail?: string;
  invitedPhone?: string;
} | null;

export type BoutDraft = {
  /** Client-side key only — a fresh uuid per bout, never sent to the API. */
  id: string;
  /** Set when the bout came from an existing matchup; sending it back preserves its votes. */
  matchupId?: string;
  isMainEvent: boolean;
  category?: string;
  rounds?: number;
  competitorA: CompetitorDraft;
  competitorB: CompetitorDraft;
  /** Text typed into a corner's search but not picked from the results. Client-side only. */
  competitorASearch?: string;
  competitorBSearch?: string;
};

export const createEmptyBout = (id: string): BoutDraft => ({
  id,
  isMainEvent: false,
  competitorA: null,
  competitorB: null,
});

const mapCompetitorToInput = (
  competitor: CompetitorDraft,
  corner: MatchupCorner
): CreateMatchupCompetitorInput => ({
  corner,
  storeId: competitor?.storeId,
  name: competitor?.name,
  invitedEmail: competitor?.invitedEmail,
  invitedPhone: competitor?.invitedPhone,
});

export const isFilledBout = (bout: BoutDraft): boolean =>
  Boolean(bout.competitorA || bout.competitorB);

const inviteEmailSchema = yup.string().trim().email();

export const isValidInviteEmail = (email?: string): boolean =>
  !email || inviteEmailSchema.isValidSync(email);

export type InvalidInvite = {
  boutNumber: number;
  corner: 'A' | 'B';
  name: string;
  email: string;
};

export const getInvalidInvites = (bouts: BoutDraft[]): InvalidInvite[] =>
  bouts.flatMap((bout, index) =>
    ([
      ['A', bout.competitorA],
      ['B', bout.competitorB],
    ] as const).flatMap(([corner, competitor]) =>
      competitor?.invitedEmail && !isValidInviteEmail(competitor.invitedEmail)
        ? [
            {
              boutNumber: index + 1,
              corner,
              name: competitor.name,
              email: competitor.invitedEmail,
            },
          ]
        : []
    )
  );

export const formatInvalidInvitesMessage = (
  invalidInvites: InvalidInvite[]
): string => {
  const list = invalidInvites
    .map(
      ({ boutNumber, corner, name, email }) =>
        `${name} (Bout ${boutNumber}, Competitor ${corner}): "${email}"`
    )
    .join(', ');

  return invalidInvites.length === 1
    ? `Invalid invite email for ${list}`
    : `Invalid invite emails for ${list}`;
};

const MISSING_COMPETITOR_HINT =
  "Pick someone from the search results, or use Invite if they're not on MILLIONS.";

type MissingCompetitor = {
  boutNumber: number;
  corner: 'A' | 'B';
  typedName?: string;
};

// The API rejects a corner with neither a store nor a name, and a bout with nothing picked is
// dropped on save, so a name typed into the search but never picked would silently vanish.
const getMissingCompetitors = (bouts: BoutDraft[]): MissingCompetitor[] =>
  bouts.flatMap((bout, index) => {
    const corners = [
      {
        corner: 'A',
        competitor: bout.competitorA,
        typedName: bout.competitorASearch?.trim(),
      },
      {
        corner: 'B',
        competitor: bout.competitorB,
        typedName: bout.competitorBSearch?.trim(),
      },
    ] as const;
    const isStarted = corners.some(
      ({ competitor, typedName }) => competitor || typedName
    );

    if (!isStarted) {
      return [];
    }

    return corners
      .filter(({ competitor }) => !competitor)
      .map(({ corner, typedName }) => ({
        boutNumber: index + 1,
        corner,
        typedName: typedName || undefined,
      }));
  });

const describeMissingCompetitor = ({
  boutNumber,
  corner,
  typedName,
}: MissingCompetitor): string =>
  typedName
    ? `"${typedName}" (Bout ${boutNumber}, Competitor ${corner})`
    : `Competitor ${corner} in Bout ${boutNumber}`;

const formatMissingCompetitorsMessage = (
  missingCompetitors: MissingCompetitor[]
): string => {
  if (missingCompetitors.length === 1) {
    return `${describeMissingCompetitor(
      missingCompetitors[0]
    )} isn't added yet. ${MISSING_COMPETITOR_HINT}`;
  }

  const list = missingCompetitors.map(describeMissingCompetitor).join('; ');

  return `Some competitors aren't added yet: ${list}. ${MISSING_COMPETITOR_HINT}`;
};

export const getMatchcardError = (bouts: BoutDraft[]): string | undefined => {
  const missingCompetitors = getMissingCompetitors(bouts);
  if (missingCompetitors.length) {
    return formatMissingCompetitorsMessage(missingCompetitors);
  }

  const invalidInvites = getInvalidInvites(bouts);
  return invalidInvites.length
    ? formatInvalidInvitesMessage(invalidInvites)
    : undefined;
};

export const mapBoutsToMatchupsInput = (
  bouts: BoutDraft[]
): CreateMatchupInput[] =>
  bouts.filter(isFilledBout).map((bout, index) => ({
    ...(bout.matchupId && { id: bout.matchupId }),
    order: index,
    isMainEvent: bout.isMainEvent,
    category: bout.category,
    rounds: bout.rounds,
    competitors: [
      mapCompetitorToInput(bout.competitorA, MatchupCorner.A),
      mapCompetitorToInput(bout.competitorB, MatchupCorner.B),
    ],
  }));
